import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { RefreshCw, Search, Copy, Check, Download, ChevronsUpDown, Plus, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  useAuditLog,
  formatAuditEntry,
  getActionDotColor,
  getStr,
  DEFAULT_FILTERS,
  type AuditEntry,
  type AuditFilters,
} from "@/hooks/useAuditLog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()} ${hh}:${mm}`;
};

const ACTION_OPTIONS: { value: string; label: string }[] = [
  { value: "suspend_company", label: "Suspend Company" },
  { value: "unsuspend_company", label: "Unsuspend Company" },
  { value: "approve_company_reactivation_request", label: "Approve Request" },
  { value: "reject_company_reactivation_request", label: "Reject Request" },
  { value: "admin_impersonate_user", label: "Start Impersonation" },
  { value: "end_impersonation", label: "End Impersonation" },
  { value: "suspend_member", label: "Suspend Member" },
  { value: "reactivate_member", label: "Reactivate Member" },
  { value: "deactivate_member", label: "Deactivate Member" },
  { value: "grant_admin_role", label: "Grant Admin Role" },
  { value: "remove_admin_role", label: "Remove Admin Role" },
  { value: "superuser_invitation_accepted", label: "Superuser Invitation Accepted" },
];

const ENTITY_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "company", label: "Company" },
  { value: "user", label: "User" },
  { value: "superuser", label: "Superuser" },
];

const csvEscape = (v: unknown): string => {
  const s = v === null || v === undefined ? "" : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadCsv = (entries: AuditEntry[]) => {
  const headers = [
    "timestamp",
    "admin_name",
    "admin_email",
    "action",
    "description",
    "entity_type",
    "entity_id",
    "target_name",
    "target_email",
    "user_role",
    "company",
  ];
  const lines = [headers.join(",")];
  for (const e of entries) {
    const role = getStr(e.details, "target_role") ?? getStr(e.details, "role") ?? "";
    lines.push(
      [
        e.created_at,
        e.admin_name,
        e.admin_email,
        e.action,
        formatAuditEntry(e),
        e.entity_type,
        e.entity_id,
        e.target_user_name ?? e.impersonated_user_name ?? "",
        e.target_user_email ?? e.impersonated_user_email ?? "",
        role,
        e.action_company_name ?? e.company_name ?? "",
      ]
        .map(csvEscape)
        .join(",")
    );
  }
  const csv = lines.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const today = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `audit-log-${today}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

interface ComboboxFilterProps {
  value: string;
  onValueChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  width?: string;
}

function ComboboxFilter({ value, onValueChange, options, placeholder, searchPlaceholder, emptyMessage, width = "w-[240px]" }: ComboboxFilterProps) {
  const [open, setOpen] = useState(false);
  const selectedLabel = value ? (options.find((o) => o.value === value)?.label ?? value) : placeholder;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`${width} h-9 flex items-center justify-between rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-slate-100 hover:bg-slate-800 focus:outline-none`}
        >
          <span className="truncate">{selectedLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 bg-slate-800 border-slate-700" style={{ width: "260px" }}>
        <Command className="bg-slate-800">
          <CommandInput placeholder={searchPlaceholder} className="text-slate-100 placeholder:text-slate-500 border-slate-700" />
          <CommandList>
            <CommandEmpty className="text-slate-400 text-sm py-3 text-center">{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={o.label}
                  onSelect={() => { onValueChange(o.value); setOpen(false); }}
                  className="text-slate-200 data-[selected=true]:bg-slate-700 cursor-pointer"
                >
                  <Check className={`mr-2 h-4 w-4 ${value === o.value ? "opacity-100" : "opacity-0"}`} />
                  {o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

type FilterType = "actions" | "admin" | "user" | "company" | "entityType" | "role" | "date";

export default function Audit() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filtersFromParams = (): AuditFilters => ({
    actions: searchParams.get("actions")?.split(",").filter(Boolean) ?? [],
    adminIds: searchParams.get("adminIds")?.split(",").filter(Boolean) ?? [],
    targetUserIds: searchParams.get("targetUserIds")?.split(",").filter(Boolean) ?? [],
    companyIds: searchParams.get("companyIds")?.split(",").filter(Boolean) ?? [],
    entityTypes: searchParams.get("entityTypes")?.split(",").filter(Boolean) ?? [],
    targetRoles: searchParams.get("targetRoles")?.split(",").filter(Boolean) ?? [],
    dateRanges: (() => {
      const raw = searchParams.get("dateRanges");
      if (!raw) return [];
      try { return JSON.parse(raw); } catch { return []; }
    })(),
    page: parseInt(searchParams.get("page") ?? "1", 10) || 1,
    pageSize: 50,
  });

  const [filters, setFilters] = useState<AuditFilters>(filtersFromParams);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [activeFilterType, setActiveFilterType] = useState<FilterType | null>(null);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (filters.actions.length) params.actions = filters.actions.join(",");
    if (filters.adminIds.length) params.adminIds = filters.adminIds.join(",");
    if (filters.targetUserIds.length) params.targetUserIds = filters.targetUserIds.join(",");
    if (filters.companyIds.length) params.companyIds = filters.companyIds.join(",");
    if (filters.entityTypes.length) params.entityTypes = filters.entityTypes.join(",");
    if (filters.targetRoles.length) params.targetRoles = filters.targetRoles.join(",");
    if (filters.dateRanges.length) params.dateRanges = JSON.stringify(filters.dateRanges);
    if (filters.page > 1) params.page = String(filters.page);
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const updateFilters = (updates: Partial<AuditFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...updates,
      page: "page" in updates ? (updates.page ?? 1) : 1,
    }));
  };

  const clearFilters = () => setFilters(DEFAULT_FILTERS);

  const hasActiveFilters =
    filters.actions.length > 0 ||
    filters.adminIds.length > 0 ||
    filters.targetUserIds.length > 0 ||
    filters.companyIds.length > 0 ||
    filters.entityTypes.length > 0 ||
    filters.targetRoles.length > 0 ||
    filters.dateRanges.length > 0;

  const { entries, total, totalPages, isLoading, isFetching, error, refetch } = useAuditLog(filters);

  useEffect(() => {
    const channel = supabase
      .channel('admin-audit-changes')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'admin_audit_logs' }, () => {
        refetch();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const { data: allCompanies = [] } = useQuery({
    queryKey: ["audit-filter-companies"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("companies")
        .select("id, name")
        .neq("id", "2c24dd37-7434-4b8a-919e-2a54fb7aae20")
        .order("name", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 60_000,
  });

  const { data: allAdmins = [] } = useQuery({
    queryKey: ["audit-filter-admins"],
    queryFn: async () => {
      const adminRes = await supabase.from("admin_users").select("id");
      if (adminRes.error) throw adminRes.error;
      const ids = (adminRes.data ?? []).map((a) => a.id);
      if (!ids.length) return [];
      const profileRes = await supabase
        .from("profiles")
        .select("id, first_name, last_name, email")
        .in("id", ids);
      if (profileRes.error) throw profileRes.error;
      return (profileRes.data ?? []).map((p) => ({
        id: p.id,
        email: p.email ?? "",
        name: `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email || "Unknown",
      }));
    },
    staleTime: 60_000,
  });

  const { data: allUsers = [] } = useQuery({
    queryKey: ["audit-filter-users"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, email")
        .order("first_name");
      if (error) throw error;
      return (data ?? []).map((p) => ({
        id: p.id,
        email: p.email ?? "",
        name: `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email || "Unknown",
      }));
    },
    staleTime: 60_000,
  });

  // Client-side text search over current page
  const visibleEntries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => {
      const hay = `${e.action} ${e.admin_name} ${e.admin_email} ${e.target_user_name ?? ""} ${e.target_user_email ?? ""} ${e.action_company_name ?? ""} ${e.company_name ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [entries, search]);

  const getEntityLabel = (e: AuditEntry): string => {
    const d = e.details as any;
    if (!d) return e.entity_id;

    // Try to find a human-readable identifier in details
    const email = d.invited_email || d.target_email || d.old_email || d.user_email || d.requester_email;
    const name = d.target_name || d.admin_name || d.company_name;

    if (name && email) return `${e.entity_id} (${name} — ${email})`;
    if (email) return `${e.entity_id} (${email})`;
    if (name) return `${e.entity_id} (${name})`;
    return e.entity_id;
  };

  const handleCopy = async (e: AuditEntry) => {
    const formattedDate = formatDateTime(e.created_at);
    const description = `${e.admin_email}: ${formatAuditEntry(e)} (Entity: ${getEntityLabel(e)})`;
    let text = `[${formattedDate}] ${description}`;
    if (e.details && Object.keys(e.details).length > 0) {
      text += `\nDetails: ${JSON.stringify(e.details, null, 2)}`;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(e.id);
      setTimeout(() => setCopiedId((c) => (c === e.id ? null : c)), 2000);
    } catch {
      // ignore
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedId((cur) => (cur === id ? null : id));
  };

  // ----- Inline filter panel -----
  const renderFilterPanel = () => {
    if (!activeFilterType) return null;
    const closeBtn = (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setActiveFilterType(null)}
        className="h-7 text-slate-300 hover:bg-slate-700"
      >
        Close
      </Button>
    );

    if (activeFilterType === "actions") {
      const selected = new Set(filters.actions);
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase text-slate-400">Action types</div>
            {closeBtn}
          </div>
          <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
            {ACTION_OPTIONS.map((o) => (
              <label key={o.value} className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                <Checkbox
                  checked={selected.has(o.value)}
                  onCheckedChange={(checked) => {
                    const next = new Set(selected);
                    if (checked) next.add(o.value);
                    else next.delete(o.value);
                    updateFilters({ actions: Array.from(next) });
                  }}
                />
                {o.label}
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (activeFilterType === "entityType") {
      const selected = new Set(filters.entityTypes);
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase text-slate-400">Entity type</div>
            {closeBtn}
          </div>
          <div className="flex flex-wrap gap-3">
            {ENTITY_TYPE_OPTIONS.map((o) => (
              <label key={o.value} className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                <Checkbox
                  checked={selected.has(o.value)}
                  onCheckedChange={(checked) => {
                    const next = new Set(selected);
                    if (checked) next.add(o.value);
                    else next.delete(o.value);
                    updateFilters({ entityTypes: Array.from(next) });
                  }}
                />
                {o.label}
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (activeFilterType === "admin") {
      const options = allAdmins.map((a) => ({ value: a.id, label: a.name + (a.email ? ` (${a.email})` : "") }));
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase text-slate-400">Add admin</div>
            {closeBtn}
          </div>
          {filters.adminIds.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {filters.adminIds.map((id) => {
                const admin = allAdmins.find((a) => a.id === id);
                return (
                  <span key={id} className="inline-flex items-center gap-1.5 rounded-full bg-slate-800 border border-slate-700 px-2.5 py-1 text-xs text-slate-200">
                    {admin?.name ?? id}
                    <button onClick={() => updateFilters({ adminIds: filters.adminIds.filter((x) => x !== id) })} className="text-slate-400 hover:text-slate-100"><X className="h-3 w-3" /></button>
                  </span>
                );
              })}
            </div>
          )}
          <ComboboxFilter
            value=""
            onValueChange={(id) => { if (!filters.adminIds.includes(id)) updateFilters({ adminIds: [...filters.adminIds, id] }); }}
            options={options}
            placeholder="Select admin…"
            searchPlaceholder="Search admins…"
            emptyMessage="No admins found"
            width="w-[280px]"
          />
        </div>
      );
    }

    if (activeFilterType === "user") {
      const options = allUsers.map((u) => ({ value: u.id, label: u.name + (u.email ? ` (${u.email})` : "") }));
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase text-slate-400">Add affected user</div>
            {closeBtn}
          </div>
          {filters.targetUserIds.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {filters.targetUserIds.map((id) => {
                const user = allUsers.find((u) => u.id === id);
                return (
                  <span key={id} className="inline-flex items-center gap-1.5 rounded-full bg-slate-800 border border-slate-700 px-2.5 py-1 text-xs text-slate-200">
                    {user?.name ?? id}
                    <button onClick={() => updateFilters({ targetUserIds: filters.targetUserIds.filter((x) => x !== id) })} className="text-slate-400 hover:text-slate-100"><X className="h-3 w-3" /></button>
                  </span>
                );
              })}
            </div>
          )}
          <ComboboxFilter
            value=""
            onValueChange={(id) => { if (!filters.targetUserIds.includes(id)) updateFilters({ targetUserIds: [...filters.targetUserIds, id] }); }}
            options={options}
            placeholder="Select user…"
            searchPlaceholder="Search users…"
            emptyMessage="No users found"
            width="w-[280px]"
          />
        </div>
      );
    }

    if (activeFilterType === "company") {
      const options = allCompanies.map((c) => ({ value: c.id, label: c.name }));
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase text-slate-400">Add company</div>
            {closeBtn}
          </div>
          {filters.companyIds.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {filters.companyIds.map((id) => {
                const company = allCompanies.find((c) => c.id === id);
                return (
                  <span key={id} className="inline-flex items-center gap-1.5 rounded-full bg-slate-800 border border-slate-700 px-2.5 py-1 text-xs text-slate-200">
                    {company?.name ?? id}
                    <button onClick={() => updateFilters({ companyIds: filters.companyIds.filter((x) => x !== id) })} className="text-slate-400 hover:text-slate-100"><X className="h-3 w-3" /></button>
                  </span>
                );
              })}
            </div>
          )}
          <ComboboxFilter
            value=""
            onValueChange={(id) => { if (!filters.companyIds.includes(id)) updateFilters({ companyIds: [...filters.companyIds, id] }); }}
            options={options}
            placeholder="Select company…"
            searchPlaceholder="Search companies…"
            emptyMessage="No companies found"
            width="w-[280px]"
          />
        </div>
      );
    }

    if (activeFilterType === "role") {
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase text-slate-400">User role</div>
            {closeBtn}
          </div>
          <div className="flex gap-4">
            {["admin", "member"].map((r) => {
              const checked = filters.targetRoles.includes(r);
              return (
                <label key={r} className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(c) => {
                      const next = new Set(filters.targetRoles);
                      if (c) next.add(r);
                      else next.delete(r);
                      updateFilters({ targetRoles: Array.from(next) });
                    }}
                  />
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </label>
              );
            })}
          </div>
        </div>
      );
    }

    if (activeFilterType === "date") {
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase text-slate-400">Date range (any range matches)</div>
            {closeBtn}
          </div>
          <div className="space-y-2">
            {filters.dateRanges.map((r, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  type="date"
                  value={r.from ?? ""}
                  onChange={(e) => updateFilters({
                    dateRanges: filters.dateRanges.map((x, j) => j === i ? { ...x, from: e.target.value || null } : x),
                  })}
                  className="h-9 w-[160px] bg-slate-900 border-slate-700 text-slate-100"
                />
                <span className="text-xs text-slate-500">to</span>
                <Input
                  type="date"
                  value={r.to ?? ""}
                  onChange={(e) => updateFilters({
                    dateRanges: filters.dateRanges.map((x, j) => j === i ? { ...x, to: e.target.value || null } : x),
                  })}
                  className="h-9 w-[160px] bg-slate-900 border-slate-700 text-slate-100"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => updateFilters({
                    dateRanges: filters.dateRanges.filter((_, j) => j !== i),
                  })}
                  className="h-9 text-slate-400 hover:text-white"
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() => updateFilters({
                dateRanges: [...filters.dateRanges, { from: null, to: null }],
              })}
              className="h-9 border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
            >
              + Add range
            </Button>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <div className="space-y-4">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search current page…"
              className="pl-8 h-9 bg-slate-900 border-slate-700 text-slate-100 placeholder:text-slate-500"
            />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="h-9 border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
              >
                <Plus className="h-3.5 w-3.5 mr-2" />
                Add filter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-slate-900 border-slate-700 text-slate-100">
              <DropdownMenuItem onClick={() => setActiveFilterType("actions")}>Action type</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setActiveFilterType("admin")}>Admin</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setActiveFilterType("user")}>Affected user</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setActiveFilterType("company")}>Company</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setActiveFilterType("entityType")}>Entity type</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setActiveFilterType("role")}>User role</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setActiveFilterType("date")}>Date range</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-slate-400">
              {total} total {isFetching ? "(loading…)" : ""}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => downloadCsv(visibleEntries)}
              disabled={visibleEntries.length === 0}
              className="h-9 border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
            >
              <Download className="h-3.5 w-3.5 mr-2" />
              Export CSV
            </Button>
            <Button
              size="icon"
              variant="outline"
              onClick={() => refetch()}
              className="h-9 w-9 border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isLoading || isFetching ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {/* Active filter pills */}
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2">
            {filters.actions.length > 0 && (
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-800 border border-slate-700 px-2.5 py-1 text-xs text-slate-200 cursor-pointer hover:bg-slate-700"
                onClick={() => setActiveFilterType(activeFilterType === 'actions' ? null : 'actions')}
              >
                Action: {filters.actions.map(a => ACTION_OPTIONS.find(o => o.value === a)?.label ?? a).join(', ')}
                <button onClick={(e) => { e.stopPropagation(); updateFilters({ actions: [] }); }} className="text-slate-400 hover:text-slate-100"><X className="h-3 w-3" /></button>
              </span>
            )}
            {filters.adminIds.map(id => {
              const admin = allAdmins.find(a => a.id === id);
              return (
                <span
                  key={`admin-${id}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-800 border border-slate-700 px-2.5 py-1 text-xs text-slate-200 cursor-pointer hover:bg-slate-700"
                  onClick={() => setActiveFilterType(activeFilterType === 'admin' ? null : 'admin')}
                >
                  Admin: {admin?.name ?? id}
                  <button onClick={(e) => { e.stopPropagation(); updateFilters({ adminIds: filters.adminIds.filter(x => x !== id) }); }} className="text-slate-400 hover:text-slate-100"><X className="h-3 w-3" /></button>
                </span>
              );
            })}
            {filters.targetUserIds.map(id => {
              const user = allUsers.find(u => u.id === id);
              return (
                <span
                  key={`user-${id}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-800 border border-slate-700 px-2.5 py-1 text-xs text-slate-200 cursor-pointer hover:bg-slate-700"
                  onClick={() => setActiveFilterType(activeFilterType === 'user' ? null : 'user')}
                >
                  User: {user?.name ?? id}
                  <button onClick={(e) => { e.stopPropagation(); updateFilters({ targetUserIds: filters.targetUserIds.filter(x => x !== id) }); }} className="text-slate-400 hover:text-slate-100"><X className="h-3 w-3" /></button>
                </span>
              );
            })}
            {filters.companyIds.map(id => {
              const company = allCompanies.find(c => c.id === id);
              return (
                <span
                  key={`company-${id}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-800 border border-slate-700 px-2.5 py-1 text-xs text-slate-200 cursor-pointer hover:bg-slate-700"
                  onClick={() => setActiveFilterType(activeFilterType === 'company' ? null : 'company')}
                >
                  Company: {company?.name ?? id}
                  <button onClick={(e) => { e.stopPropagation(); updateFilters({ companyIds: filters.companyIds.filter(x => x !== id) }); }} className="text-slate-400 hover:text-slate-100"><X className="h-3 w-3" /></button>
                </span>
              );
            })}
            {filters.entityTypes.length > 0 && (
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-800 border border-slate-700 px-2.5 py-1 text-xs text-slate-200 cursor-pointer hover:bg-slate-700"
                onClick={() => setActiveFilterType(activeFilterType === 'entityType' ? null : 'entityType')}
              >
                Type: {filters.entityTypes.join(', ')}
                <button onClick={(e) => { e.stopPropagation(); updateFilters({ entityTypes: [] }); }} className="text-slate-400 hover:text-slate-100"><X className="h-3 w-3" /></button>
              </span>
            )}
            {filters.targetRoles.length > 0 && (
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-800 border border-slate-700 px-2.5 py-1 text-xs text-slate-200 cursor-pointer hover:bg-slate-700"
                onClick={() => setActiveFilterType(activeFilterType === 'role' ? null : 'role')}
              >
                Role: {filters.targetRoles.join(', ')}
                <button onClick={(e) => { e.stopPropagation(); updateFilters({ targetRoles: [] }); }} className="text-slate-400 hover:text-slate-100"><X className="h-3 w-3" /></button>
              </span>
            )}
            {filters.dateRanges.map((r, i) => {
              const label = r.from && r.to
                ? `Date: ${r.from} to ${r.to}`
                : r.from
                ? `Date: from ${r.from}`
                : r.to
                ? `Date: until ${r.to}`
                : `Date: any`;
              return (
                <span
                  key={`date-${i}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-800 border border-slate-700 px-2.5 py-1 text-xs text-slate-200 cursor-pointer hover:bg-slate-700"
                  onClick={() => setActiveFilterType(activeFilterType === 'date' ? null : 'date')}
                >
                  {label}
                  <button onClick={(e) => { e.stopPropagation(); updateFilters({ dateRanges: filters.dateRanges.filter((_, j) => j !== i) }); }} className="text-slate-400 hover:text-slate-100"><X className="h-3 w-3" /></button>
                </span>
              );
            })}
            <Button
              size="sm"
              variant="ghost"
              onClick={clearFilters}
              className="h-7 text-slate-300 hover:bg-slate-700"
            >
              Clear all
            </Button>
          </div>
        )}

        {/* Inline filter panel */}
        {renderFilterPanel()}

        {/* Timeline */}
        <div className="bg-slate-900 border border-slate-800 rounded-md">
          {isLoading && (
            <ul className="divide-y divide-slate-800">
              {Array.from({ length: 10 }).map((_, i) => (
                <li key={i} className="flex items-center gap-3 px-4 py-3">
                  <Skeleton className="h-2 w-2 rounded-full bg-slate-800 shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-2/3 bg-slate-800" />
                    <Skeleton className="h-3 w-32 bg-slate-800" />
                  </div>
                  <Skeleton className="h-3 w-32 bg-slate-800 shrink-0" />
                </li>
              ))}
            </ul>
          )}

          {!isLoading && error && (
            <div className="text-center py-12 px-4 space-y-3">
              <p className="text-sm text-red-400">{error}</p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => refetch()}
                className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
              >
                <RefreshCw className="h-3.5 w-3.5 mr-2" />
                Retry
              </Button>
            </div>
          )}

          {!isLoading && !error && visibleEntries.length === 0 && (
            <div className="text-center py-12 text-sm text-slate-400 space-y-3">
              {hasActiveFilters || search ? (
                <>
                  <div>No entries match your filters.</div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSearch("");
                      clearFilters();
                    }}
                    className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
                  >
                    Clear filters
                  </Button>
                </>
              ) : (
                <div>No audit log entries yet.</div>
              )}
            </div>
          )}

          {!isLoading && !error && visibleEntries.length > 0 && (
            <ul className="divide-y divide-slate-800">
              {visibleEntries.map((entry) => {
                const isExpanded = expandedId === entry.id;
                const isCopied = copiedId === entry.id;
                const targetName =
                  entry.target_user_name ??
                  entry.impersonated_user_name ??
                  entry.company_name;
                const targetEmail =
                  entry.target_user_email ?? entry.impersonated_user_email;
                return (
                  <li key={entry.id} className="hover:bg-slate-800/50 transition-colors">
                    <div
                      className="flex items-start gap-3 px-4 py-3 cursor-pointer"
                      onClick={() => toggleExpand(entry.id)}
                    >
                      <span
                        className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${getActionDotColor(entry.action)}`}
                        aria-hidden
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white break-words">
                          {formatAuditEntry(entry)}
                        </div>
                        {entry.admin_email && (
                          <div className="text-xs text-slate-400 mt-0.5">{entry.admin_email}</div>
                        )}
                      </div>
                      <div className="text-xs text-slate-400 shrink-0 text-right whitespace-nowrap">
                        {formatDateTime(entry.created_at)}
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopy(entry);
                        }}
                        className="h-7 w-7 shrink-0 text-slate-400 hover:text-slate-100 hover:bg-slate-700"
                        title={isCopied ? "Copied" : "Copy entry"}
                      >
                        {isCopied ? (
                          <Check className="h-3.5 w-3.5 text-emerald-400" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </div>

                    {isExpanded && (
                      <div className="px-4 pb-4 pt-0 ml-7 border-l border-slate-800">
                        <dl className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-2 text-xs">
                          <dt className="text-slate-500">Action</dt>
                          <dd className="text-slate-200">{formatAuditEntry(entry)}</dd>

                          <dt className="text-slate-500">Admin</dt>
                          <dd className="text-slate-200">
                            {entry.admin_name}
                            {entry.admin_email ? (
                              <span className="text-slate-400"> ({entry.admin_email})</span>
                            ) : null}
                          </dd>

                          {(targetName || targetEmail) && (
                            <>
                              <dt className="text-slate-500">Target</dt>
                              <dd className="text-slate-200">
                                {targetName ?? "—"}
                                {targetEmail ? (
                                  <span className="text-slate-400"> ({targetEmail})</span>
                                ) : null}
                              </dd>
                            </>
                          )}

                          {entry.action_company_name && (
                            <>
                              <dt className="text-slate-500">Company</dt>
                              <dd className="text-slate-200">{entry.action_company_name}</dd>
                            </>
                          )}

                          {(getStr(entry.details, "notes") || getStr(entry.details, "reason") || getStr(entry.details, "suspension_reason")) && (
                            <>
                              <dt className="text-slate-500">Notes / Reason</dt>
                              <dd className="text-slate-200">
                                {getStr(entry.details, "notes") ?? getStr(entry.details, "reason") ?? getStr(entry.details, "suspension_reason")}
                              </dd>
                            </>
                          )}

                          <dt className="text-slate-500">Entity ID</dt>
                          <dd className="text-slate-500 font-mono text-[11px] break-all">
                            {entry.entity_id}
                          </dd>

                          <dt className="text-slate-500">Timestamp</dt>
                          <dd className="text-slate-200">{formatDateTime(entry.created_at)}</dd>

                          <dt className="text-slate-500">Raw details</dt>
                          <dd>
                            <pre className="bg-slate-950 text-slate-300 font-mono text-[11px] p-3 rounded border border-slate-800 max-h-[200px] overflow-y-auto whitespace-pre-wrap break-all">
{JSON.stringify(entry.details ?? {}, null, 2)}
                            </pre>
                          </dd>
                        </dl>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Pagination */}
        {!isLoading && !error && total > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">{total} total entries</span>
            <div className="flex items-center gap-3">
              <Button
                size="sm"
                variant="outline"
                disabled={filters.page <= 1}
                onClick={() => updateFilters({ page: filters.page - 1 })}
                className="h-7 border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"
              >
                Previous
              </Button>
              <span className="text-xs text-slate-400">
                Page {filters.page} of {totalPages}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={filters.page >= totalPages}
                onClick={() => updateFilters({ page: filters.page + 1 })}
                className="h-7 border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
